import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { APIError } from "../../../constants/types";
import { ERROR_MESSAGES } from "../../../constants/enums";
import { showMessage } from "../../messages/messagesActions";
import {
  buildIftaImportFormData,
  postIftaImportPreview,
} from "../../../helpers/iftaImportUpload";
import { IIftaImportPreview, ImportMode } from "./iftaImport.types";

/**
 * Preview thunk for the IFTA quarterly import.
 *
 * Builds the multipart FormData via `buildIftaImportFormData` and POSTs it to the
 * preview endpoint via `postIftaImportPreview`. On success it returns the preview
 * payload (`res.data.data`). On failure it surfaces an error snackbar and rejects
 * with an `APIError` payload that carries the HTTP status so the reducer can detect
 * a 404 and hide the entry points (Requirement 3.6).
 */
export const previewIftaImportAsync = createAsyncThunk<
  IIftaImportPreview,
  {
    files: File[];
    customerPolicyId?: number;
    leadId?: string | null;
    incrementalNumber: number;
    mode: ImportMode;
    expectedQuarters?: number;
  },
  { rejectValue: APIError }
>("iftaImport/preview", async (args, { rejectWithValue, dispatch }) => {
  try {
    const fd = buildIftaImportFormData(args);
    const res = await postIftaImportPreview(fd);
    return res.data.data as IIftaImportPreview;
  } catch (err: any) {
    const status = err.response?.status;
    const message = err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR;
    // A 404 means the preview endpoint is not deployed. This should degrade
    // silently by hiding the entry points (Req 3.6) rather than alarming the
    // user with an error snackbar. Only genuine failures (non-404) surface the
    // generic error snackbar (Req 3.5).
    if (status !== 404) {
      dispatch(
        showMessage({
          type: "error",
          message,
          displayAs: "snackbar",
        }),
      );
    }
    return rejectWithValue({
      message,
      status,
    });
  }
});

// Sync actions
export const setIftaMode = createAction<ImportMode>("iftaImport/setMode");
export const setIftaFiles = createAction<File[]>("iftaImport/setFiles");
export const ackIftaWarning = createAction<{ key: string; file_name?: string }>(
  "iftaImport/ackWarning",
);
export const applyIftaEdit = createAction<{ path: string; value: unknown }>(
  "iftaImport/applyEdit",
);
export const resetIftaImport = createAction("iftaImport/reset");
